import moment from 'moment'
import {
  createReportDataSourceMap,
  getReportBindValue,
} from './reportBind.util'

export const REPORT_RESOURCE_TYPE = 'report_resource'
export const REPORT_RESOURCE_OUTPUT_FIELDS = [
  'printStyle',
  'pdfStickerStyle',
  'exportExcel',
  'rotateOnPrint',
  'autoPrint',
]

const cloneReportData = (value) => JSON.parse(JSON.stringify(value || {}))

export const normalizeReportResourceCode = (value) =>
  `${value || ''}`.trim()

export const isReportResourceOutput = (reportData = {}) => {
  const webviewData = reportData.webviewData || reportData
  const publishData = webviewData.publishData || webviewData

  return publishData.resource === true || publishData.resource === 'true'
}

export const normalizeReportResourcePublishData = (publishData = {}) => {
  const nextPublishData = { ...publishData }
  if (!isReportResourceOutput(nextPublishData)) return nextPublishData

  REPORT_RESOURCE_OUTPUT_FIELDS.forEach((field) => {
    nextPublishData[field] = false
  })
  nextPublishData.delayPrintSec = ''
  delete nextPublishData.outputMode
  delete nextPublishData.folderId

  return nextPublishData
}

export const setReportResourceOutput = (publishData = {}, enabled = false) =>
  normalizeReportResourcePublishData({
    ...publishData,
    resource: enabled,
  })

export const getReportResourceCode = (item = {}) =>
  normalizeReportResourceCode(
    item.resourceCode ||
      item.resourceLink?.reportCode ||
      item.resourceLink?.sourceReportCode,
  )

export const getReportResourceItems = (reportData = {}) => {
  const webviewData = reportData.webviewData || reportData
  const printData = webviewData.printData || webviewData

  return Object.values(printData || {}).filter(
    (item) => item?.type === REPORT_RESOURCE_TYPE,
  )
}

export const getReportResourceCodes = (reportData = {}) =>
  [
    ...new Set(
      getReportResourceItems(reportData)
        .map(getReportResourceCode)
        .filter(Boolean),
    ),
  ]

export const isReportItemVisibleOnLayoutPage = (item = {}, page = 1) =>
  (item.page || 1) === page ||
  (item.type === REPORT_RESOURCE_TYPE && item.showOnEveryPage === true)

export const getReportResourceSize = (layoutData = {}) => {
  const isLandscape = layoutData.orientation === 'landscape'

  return {
    width: layoutData.width || (isLandscape ? '29.7cm' : '21cm'),
    height: layoutData.height || (isLandscape ? '21cm' : '29.7cm'),
  }
}

export const buildReportResourceItem = (reportData = {}) => {
  const reportCode = normalizeReportResourceCode(
    reportData.reportCode || reportData.reportTemplate?.code,
  )
  const size = getReportResourceSize(reportData.layoutData)

  if (!reportCode) return null

  return {
    name: reportCode,
    value: reportCode,
    type: REPORT_RESOURCE_TYPE,
    resourceCode: reportCode,
    width: size.width,
    height: size.height,
  }
}

export const normalizePortableReportResources = (reportData = {}) => {
  const resourceItems = getReportResourceItems(reportData)

  resourceItems.forEach((item) => {
    const resourceCode = getReportResourceCode(item)

    item.resourceCode = resourceCode
    item.name = resourceCode
    item.value = resourceCode

    delete item.resourceLink
    delete item.sourceReportTemplateId
    delete item.resourceReportTemplateId
    delete item.resolvedReportTemplateId
    delete item.resourceReportName
    delete item.resourceStatus
    delete item.resourceData
    delete item.lastResolvedAt
  })

  return reportData
}

const isIsoDate = (value) => {
  if (typeof value !== 'string') return false
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(value)) {
    return false
  }

  const date = new Date(value)
  return !Number.isNaN(date.getTime()) && date.toISOString() === value
}

export const resolveReportResourceData = (reportData = {}, params = {}) => {
  const nextReportData = cloneReportData(reportData)
  const dataSourceList = nextReportData.dataSourceData?.list || []
  const dataSourceMap = createReportDataSourceMap(dataSourceList)
  const printData = nextReportData.printData || {}
  let syFormResult

  Object.keys(printData).forEach((key) => {
    const item = printData[key]
    const bindValue = getReportBindValue(dataSourceMap, item.name)

    if (typeof bindValue !== 'undefined') {
      item.value = bindValue
      if (item.name === 'syformresult') {
        syFormResult = item.value
        delete printData[key]
      }
    } else if (
      typeof dataSourceMap[item.name] === 'undefined' &&
      item.type === 'object'
    ) {
      item.value = ''
    }
  })

  Object.keys(printData).forEach((key) => {
    const item = printData[key]

    if (syFormResult && syFormResult[item.value?.name]) {
      if (syFormResult[item.value.name + '-Comment']) {
        item.comment = syFormResult[item.value.name + '-Comment']
      }
      item.type = item.value.type
      item.choices = item.value.choices
      item.value = syFormResult[item.value.name]
    } else if (
      item.value &&
      typeof item.value === 'object' &&
      !Array.isArray(item.value)
    ) {
      item.value = item.value.title
    }

    if (
      ['table', 'advance_table'].includes(item.type) &&
      Number(item.dataSourceId) > 0
    ) {
      item.type = 'advance_table_result'
      item.value = dataSourceMap._table[item.dataSourceId] || []
      item.value.forEach((row) => {
        Object.keys(row || {}).forEach((fieldName) => {
          if (isIsoDate(row[fieldName])) {
            row[fieldName] = moment(row[fieldName])
              .add(543, 'year')
              .format('D MMMM YYYY')
          }
        })
      })
    } else if (item.type === 'choice') {
      if (dataSourceMap._dsName[item.name]) {
        item.value = dataSourceMap._dsName[item.name]
      }
    } else if (item.type === 'object' && isIsoDate(item.value)) {
      item.type = 'object_date'
      item.format = 'D MMMM YYYY'
    } else if (item.type === 'search_param') {
      const paramName = `${item.name || ''}`.replace(/[{}]/g, '')
      item.value = params[paramName] || ''
    } else if (item.type === 'vital_sign') {
      item.propsList = item.dataSourceId
        ? dataSourceMap._table[item.dataSourceId] || []
        : []
    }
  })

  return {
    ...nextReportData,
    printData,
    dataSourceData: {
      ...(nextReportData.dataSourceData || {}),
      list: dataSourceList,
    },
  }
}
